import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Settings, Users, Globe, Edit, Check } from 'lucide-react';

export type WebsiteStatus = 'pending' | 'in-progress' | 'live';

interface MyWebsiteStatusComponentProps {
  status: WebsiteStatus;
  subdomain?: string;
  onEdit: () => void;
}

const steps = [
  {
    id: 'setup',
    title: 'Setup Details',
    icon: Settings,
    description: 'Basic configuration'
  },
  {
    id: 'configuration',
    title: 'Configure Website In-Progress',
    icon: Users,
    description: 'Content and settings'
  },
  {
    id: 'live',
    title: 'Live Website',
    icon: Globe,
    description: 'Available to customers'
  }
];

const getStepStatus = (stepIndex: number, currentStatus: WebsiteStatus) => {
  switch (currentStatus) {
    case 'pending':
      return stepIndex === 0 ? 'current' : 'upcoming';
    case 'in-progress':
      if (stepIndex === 0) return 'completed';
      if (stepIndex === 1) return 'current';
      return 'upcoming';
    case 'live':
      return stepIndex < 2 ? 'completed' : 'current';
    default:
      return 'upcoming';
  }
};

const MyWebsiteStatusComponent: React.FC<MyWebsiteStatusComponentProps> = ({
  status,
  subdomain,
  onEdit
}) => {
  const getStatusColor = (status: WebsiteStatus) => {
    switch (status) {
      case 'pending':
        return 'text-muted-foreground';
      case 'in-progress':
        return 'text-primary';
      case 'live':
        return 'text-green-600';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusText = (status: WebsiteStatus) => {
    switch (status) {
      case 'pending':
        return 'Setup Pending';
      case 'in-progress':
        return 'Configuration In Progress';
      case 'live':
        return 'Website Live';
      default:
        return 'Unknown Status';
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Website Status</h2>
            <p className={`text-sm font-medium ${getStatusColor(status)}`}>
              {getStatusText(status)}
            </p>
            {subdomain && status === 'live' && (
              <p className="text-sm text-muted-foreground mt-1">
                Available at: <span className="text-primary font-medium">{subdomain}.bookyourzone.com</span>
              </p>
            )}
          </div>
          <Button onClick={onEdit} variant="outline" size="sm" className="flex items-center gap-2">
            <Edit className="h-4 w-4" />
            Edit Website
          </Button>
        </div>

        {/* Progress Stepper */}
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-6 left-6 right-6 h-0.5 bg-border">
            <div 
              className="h-full bg-primary transition-all duration-500 ease-out"
              style={{ 
                width: status === 'pending' ? '0%' : 
                       status === 'in-progress' ? '50%' : '100%' 
              }}
            />
          </div>

          {/* Steps */}
          <div className="relative flex justify-between">
            {steps.map((step, index) => {
              const stepStatus = getStepStatus(index, status);
              const Icon = step.icon;
              
              return (
                <div key={step.id} className="flex flex-col items-center">
                  {/* Step Circle */}
                  <div
                    className={`
                      relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300
                      ${stepStatus === 'completed' 
                        ? 'bg-primary border-primary text-primary-foreground' 
                        : stepStatus === 'current'
                        ? 'bg-primary border-primary text-primary-foreground shadow-lg'
                        : 'bg-background border-border text-muted-foreground'
                      }
                    `}
                  >
                    {stepStatus === 'completed' ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  
                  {/* Step Content */}
                  <div className="mt-3 text-center max-w-[200px]">
                    <h3 className={`text-xs sm:text-sm font-medium ${
                      stepStatus === 'completed' || stepStatus === 'current'
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                    }`}>
                      {step.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Status Message */}
        <div className="mt-6 p-4 rounded-lg bg-muted/50">
          <p className="text-sm text-muted-foreground">
            {status === 'pending' && "Complete your website setup to get started."}
            {status === 'in-progress' && "Your website is being configured, it will take 24-48 hours for verification."}
            {status === 'live' && "🎉 Your website is live and ready to accept bookings!"}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default MyWebsiteStatusComponent;